import { type Filter, FilterId, FilterType } from '../types/filters';
import { getFormattedDateString } from '../utils/dateUtils';

export type FormatFilterContext = {
  currencySymbol?: string;
  exchangeRate?: number;
  dateGranularity?: 'week' | 'month';
};

const formatList = (values: string[]) => {
  const count = values.length;
  if (count === 0) return '';
  if (count <= 1) return values[0] ?? '';
  if (count <= 3) return values.join(', ');
  const displayed = values.slice(0, 3).join(', ');
  return `${displayed} +${count - 3} more`;
};

export function formatFilterDisplay(
  filter: Filter,
  ctx: FormatFilterContext = {}
): string {
  const { currencySymbol, exchangeRate = 1, dateGranularity = 'month' } = ctx;

  switch (filter.type) {
    case FilterType.DATE:
      if ('value' in filter && filter.value) {
        return getFormattedDateString(filter.value, dateGranularity);
      }
      return '';

    case FilterType.DATE_RANGE:
      if ('min' in filter && 'max' in filter && filter.min && filter.max) {
        const s = getFormattedDateString(filter.min, dateGranularity);
        const e = getFormattedDateString(filter.max, dateGranularity);
        return s && e ? `${s} to ${e}` : '';
      }
      return '';

    case FilterType.RANGE: {
      if (!('min' in filter) || !('max' in filter)) return '';
      const min = filter.min;
      const max = filter.max;
      const isPay = filter.id === FilterId.PAY;
      const fmt = (v: number) =>
        isPay && currencySymbol
          ? `${currencySymbol}${Math.round(v * exchangeRate).toLocaleString()}`
          : String(v);

      const hasMin = typeof min === 'number' && (!isPay || min > 0);
      const hasMax = typeof max === 'number';

      if (hasMin && hasMax) {
        if (min === max) return fmt(min);
        return `${fmt(min)} to ${fmt(max)}`;
      }
      if (hasMin) return `greater than ${fmt(min)}`;
      if (hasMax) return `less than ${fmt(max)}`;
      return '';
    }

    case FilterType.STATIC_LIST:
    case FilterType.LIST: {
      if (!('selections' in filter)) return '';
      return formatList(filter.selections.map((s) => s.name));
    }

    case FilterType.TREE: {
      const selections = filter.selections;
      if (selections.length > 0) {
        return formatList(selections.map((s) => s.name));
      }
      const primarySelections = filter.primarySelections;
      if (primarySelections?.length) {
        const activeNames = primarySelections
          .filter((s) => s.isActive)
          .map((s) => s.name);
        if (activeNames.length > 0) {
          return formatList(activeNames);
        }
      }
      return '';
    }

    case FilterType.TEXT:
      if ('value' in filter && filter.value != null) {
        return String(filter.value);
      }
      return '';

    case FilterType.FREE_TEXT:
      if ('text' in filter) return formatList(filter.text);
      return '';

    case FilterType.SCHOOL:
      if ('schoolResultItems' in filter) {
        return formatList(
          Object.values(filter.schoolResultItems).map((s) => s.name)
        );
      }
      return '';

    case FilterType.VALUE: {
      const v = (filter as { value?: unknown }).value;
      if (v == null) return '';
      if (typeof v === 'string' || typeof v === 'number') return String(v);
      const obj = v as Record<string, unknown>;
      if (typeof obj.name === 'string') return obj.name;
      if (typeof obj.shortName === 'string') return obj.shortName;
      if (typeof obj.longName === 'string') return obj.longName;
      return '';
    }

    default:
      return '';
  }
}
